import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

const MultiSelectDropdown = ({ options, selected, setSelected }) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const ref = useRef(null);
	const dropdownRef = useRef(null);
	const [style, setStyle] = useState({});

	const isAllSelected =
		selected.length === options.length && options.length > 0;

	useEffect(() => {
		if (open && ref.current) {
			const rect = ref.current.getBoundingClientRect();
			setStyle({
				position: "absolute",
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
				width: rect.width,
				zIndex: 9999,
			});
		}
	}, [open]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				ref.current &&
				!ref.current.contains(event.target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				closeDropdown()
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const closeDropdown = () => {
		setOpen(false);
		setSearch("");
	};

	const toggleOption = (id) => {
		setSelected((prev) =>
			prev.includes(id)
				? prev.filter((x) => x !== id)
				: [...prev, id]
		);
	};
	const toggleGroup = (usertype = 'raiser') => {
		const raiserIds = options
			.filter((user) => user.type === usertype)
			.map((user) => user.id);

		const isAllSelected = raiserIds.every((id) =>
			selected.includes(id)
		);

		setSelected(isAllSelected ? [] : raiserIds);
	};

	const toggleSelectAll = () => {
		setSelected(isAllSelected ? [] : options.map((o) => o.id));
	};

	// 🔍 Filtered options based on search
	const filteredOptions = useMemo(() => {
		return options.filter((user) =>
			user.name.toLowerCase().includes(search.toLowerCase())
		);
	}, [options, search]);

	const hasRaiser = options.some((u) => u.type === "raiser");
	const hasDeal = options.some((u) => u.type === "deal");

	return (
		<>
			<div
				ref={ref}
				className="dropdown-header"
				onClick={() => setOpen(!open)}
			>
				{selected.length === 0
					? "Select users"
					: `${selected.length} selected`}
				<span className="arrow">{open ? "▲" : "▼"}</span>
			</div>

			{open &&
				createPortal(
					<div ref={dropdownRef} className="dropdown-list" style={style}>
						{/* 🔍 Search input */}
						<input
							type="text"
							placeholder="Search users"
							className="dropdown-search"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>

						<label className="dropdown-item">
							<input
								type="checkbox"
								checked={isAllSelected}
								onChange={toggleSelectAll}
							/>
							Select All
						</label>

						{hasRaiser && (
							<label className="dropdown-item">
								<input
									type="checkbox"
									onChange={() => toggleGroup()}
									checked={options
										.filter((u) => u.type === "raiser")
										.every((u) => selected.includes(u.id))}
								/>
								Select all borrowers
							</label>
						)}

						{hasDeal && (
							<label className="dropdown-item">
								<input
									type="checkbox"
									onChange={() => toggleGroup('deal')}
									checked={options
										.filter((u) => u.type === "deal")
										.every((u) => selected.includes(u.id))}
								/>
								Select all lenders
							</label>
						)}

						{filteredOptions.length === 0 && (
							<div className="dropdown-empty" style={{ margin: "8px" }}>No users found</div>
						)}

						{filteredOptions.map((user) => (
							<label key={user.id} className="dropdown-item">
								<input
									type="checkbox"
									checked={selected.includes(user.id)}
									onChange={() => toggleOption(user.id)}
								/>
								{user.name}
							</label>
						))}
					</div>,
					document.body
				)}
		</>
	);
};

export default MultiSelectDropdown;
